/**
 * 정태홍님이 만든 tree 관련 함수들
 */

// tslint:disable
// tslint:disable: quotemark

import * as d3 from 'd3';
import * as math from 'mathjs';

export function preorder(node, visitor) {
  console.assert(!!node);

  visitor(node);

  if (node.children) {
    console.assert(2 == node.children.length);

    if (node.children[0]) preorder(node.children[0], visitor);

    if (node.children[1]) preorder(node.children[1], visitor);
  }
}

export function postorder(node, visitor) {
  console.assert(!!node);

  let results: any[] = [];

  if (node.children) {
    console.assert(2 == node.children.length && node.children.every(c => !!c));

    const leftChild = node.children[0];

    const rightChild = node.children[1];

    const leftResults = postorder(leftChild, visitor);

    const rightResults = postorder(rightChild, visitor);

    if (leftResults) results.push(...leftResults);

    if (rightResults) results.push(...rightResults);
  }

  return visitor(node, results);
}

export function getRgbColor(d) {
  if (!d) {
    throw new Error("'d' must be defined.");
  }
  const rgb = d.data ? d.data.rgb : d.rgb;
  if (!rgb) {
    throw new Error("'rgb' must be defined in either 'd' or 'd.data'.");
  }
  if (!Array.isArray(rgb)) {
    throw new Error("'rgb' must be an array.");
  }
  if (3 !== rgb.length) {
    throw new Error("'rgb' must have three elements.");
  }
  return `rgb(${rgb.join(',')})`;
}

export function getHeight(root) {
  if (!root) {
    return 0;
  }
  if (!root.children || 0 === root.children.length) {
    return 0;
  }
  return 1 + Math.max(getHeight(root.children[0]), getHeight(root.children[1]));
}

export function getHeightDifference(root) {
  if (!root || !root.children || 0 === root.children.length) {
    return 0;
  }
  return Math.abs(getHeight(root.children[0]) - getHeight(root.children[1]));
}

export function isBalanced(root) {
  if (!root) {
    return true;
  }
  return (
    1 >= getHeightDifference(root) &&
    isBalanced(root.children[0]) &&
    isBalanced(root.children[1])
  );
}

export function rotate(root, RS, parent) {
  const OS = +!RS;
  const PS = parent.children.indexOf(root);
  const pivot = root.children[OS];
  const child = pivot.children[RS];
  // 1. detach root from parent
  if (root.hasOwnProperty('parent')) { root.parent = null; }
  parent.children[PS] = null;
  // 2. detach pivot from root
  if (pivot.hasOwnProperty('parent')) { pivot.parent = null; }
  root.children[OS] = null;
  // 3. detach child from pivot
  if (child.hasOwnProperty('parent')) { child.parent = null; }
  pivot.children[RS] = null;
  // 4. attach child to root
  if (child.hasOwnProperty('parent')) { child.parent = root; }
  root.children[OS] = child;
  // 5. attach root to pivot
  if (root.hasOwnProperty('parent')) { root.parent = pivot; }
  pivot.children[RS] = root;
  // 6. attach pivot to root
  if (pivot.hasOwnProperty('parent')) { pivot.parent = parent; }
  parent.children[PS] = pivot;
}

export function getLeaves(root) {
  console.assert(!!root);

  if (!root.children) return root;

  return [getLeaves(root.children[0]), getLeaves(root.children[1])].flat();
}

// a is either a node or an array of leaves
export function getSamples(a) {
  const leaves = Array.isArray(a) ? a : getLeaves(a);

  console.assert(leaves.every(leaf => !!leaf.Samples));

  // samplesList := [ <samples>, <samples>, ..., <samples> ]
  const samplesList = leaves.map(leaf => leaf.Samples);

  const samples = samplesList.flat();

  return samples;
}

// a is either a node or an array of samples
export function getCentroid(a) {
  const samples = Array.isArray(a) ? a : getSamples(a);

  console.assert(
    0 < samples.length && samples.every(sample => 3 === sample.length)
  );

  // compute the row-wise (per-column) average
  const centroid = math.mean(samples, 0);

  // an array with 3 elements
  // it is compatible with mathjs matrix
  return centroid;
}

export function getCentroids(root) {
  const leaves = getLeaves(root);

  // centroids := [ <centroid>, <centroid>, ..., <centroid> ]
  const centroids = leaves.map(getCentroid);

  return centroids;
}

export function ward(sseS, sseT, sseU) {
  return math.sqrt(2 * (sseU - sseS - sseT));
}

export function wardOf(node) {
  let [leftChild, rightChild] = [...node.children];
  return ward(leftChild.Sse, rightChild.Sse, node.Sse);
}

// samples is a MxN matrix containing M-rows
// each of them corresponds to a N-dim. sample
export function sse(samples, sampleMean) {
  const { matrix, mean, reshape, multiply, ones, sum, square, subtract } = math;
  samples = matrix(samples);
  const [n, d] = samples.size();

  if (n < 2) return 0;

  if (!sampleMean) sampleMean = mean(samples, 0);
  sampleMean = reshape(matrix(sampleMean), [1, d]);
  sampleMean = multiply(ones([n, 1]), sampleMean);
  // @ts-ignore
  return sum(sum(square(subtract(samples, sampleMean)), 1));
}

export function sseOf(node) {
  if (
    !!node &&
    !!node.Leaves &&
    Array.isArray(node.Leaves) &&
    0 < node.Leaves.length
  ) {
    console.assert(node.Leaves.every(leaf => !!leaf.Centroid));
    // @ts-ignore
    return sse(node.Leaves.map(leaf => leaf.Centroid));
  }
  return 0;
}

export function cov(samples, sampleMean) {
  samples = math.matrix(samples);

  const [n, d] = samples.size();

  console.assert(n >= d);

  if (!sampleMean) sampleMean = math.mean(samples, 0);

  sampleMean = math.reshape(math.matrix(sampleMean), [1, d]);

  sampleMean = math.multiply(math.ones([n, 1]), sampleMean);

  const X = math.subtract(samples, sampleMean);

  // @ts-ignore
  return math.multiply(math.multiply(math.transpose(X), X), 1.0 / (n - 1));
}

export function lab2lin(x) {
  // https://github.com/d3/d3-color/blob/master/src/lab.js#L5
  // https://observablehq.com/@mbostock/lab-and-rgb
  const K = 18,
    Xn = 0.96422,
    Yn = 1,
    Zn = 0.82521,
    t0 = 4 / 29,
    t1 = 6 / 29,
    t2 = 3 * t1 * t1,
    t3 = t1 * t1 * t1;

  // https://github.com/d3/d3-color/blob/master/src/lab.js#L72
  function lab2xyz(t) {
    return t > t1 ? t * t * t : t2 * (t - t0);
  }

  return (
    // https://github.com/d3/d3-color/blob/master/src/lab.js#L52
    function f(this: any) {
      var y = (this.l + 16) / 116,
        x = isNaN(this.a) ? y : y + this.a / 500,
        z = isNaN(this.b) ? y : y - this.b / 200;
      x = Xn * lab2xyz(x);
      y = Yn * lab2xyz(y);
      z = Zn * lab2xyz(z);
      return [
        3.1338561 * x - 1.6168667 * y - 0.4906146 * z,
        -0.9787684 * x + 1.9161415 * y + 0.033454 * z,
        0.0719453 * x - 0.2289914 * y + 1.4052427 * z
      ];
    }.apply(d3.lab.apply(null, x))
  );
}

// https://github.com/d3/d3-color/blob/master/src/lab.js#L72
export function lab2xyz(t) {
  // https://github.com/d3/d3-color/blob/master/src/lab.js#L6
  const t0 = 4 / 29,
    t1 = 6 / 29,
    t2 = 3 * t1 * t1;
  return t > t1 ? t * t * t : t2 * (t - t0);
}

export function getMu(node) {
  return lab2lin(node.Centroid);
}

export function setLeaves(node, leaves) {
  const isLeaf = !leaves || !leaves.length;
  if (isLeaf) {
    node.Sse = node.Ward = 0;
    leaves.push(node);
  } /* if ( isInternal ) */ else {
    const samples = getSamples(leaves);
    const centroid = getCentroid(samples);
    node.Leaves = leaves;
    node.Samples = samples;
    node.Centroid = centroid;
    node.Sse = sseOf(node);
    node.Ward = wardOf(node);
  }
  // node.Sigma = cov( node.Samples, node.Centroid )
  return leaves;
}
